/**
 * Custom hook for submission action handlers
 */

import { useCallback, useState } from 'react';
import { submissionsApi } from '../../api/modules/submissions';
import type { FormSubmission } from '../../types/submissions';

interface UseSubmissionsHandlersProps {
  siteId: string | undefined;
  updateSubmissionInList: (submission: FormSubmission) => void;
  removeSubmissionFromList: (submissionId: string) => void;
  setSelectedSubmission: (submission: FormSubmission | null) => void;
  loadSubmissions: () => void;
}

export const useSubmissionsHandlers = ({
  siteId,
  updateSubmissionInList,
  removeSubmissionFromList,
  setSelectedSubmission,
  loadSubmissions,
}: UseSubmissionsHandlersProps) => {
  const [isMarkingRead, setIsMarkingRead] = useState(false);
  const [isToggling, setIsToggling] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [toast, setToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);

  const showToast = useCallback((message: string, type: 'success' | 'error') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  }, []);

  // Mark submission as read
  const handleMarkRead = useCallback(
    async (submission: FormSubmission) => {
      if (!siteId || submission.isRead) return;

      setIsMarkingRead(true);
      try {
        const updated = await submissionsApi.markAsRead(siteId, submission.id);
        updateSubmissionInList(updated);
      } catch (error) {
        console.error('Failed to mark as read:', error);
        showToast('Failed to mark as read', 'error');
      } finally {
        setIsMarkingRead(false);
      }
    },
    [siteId, updateSubmissionInList, showToast]
  );

  // Toggle archive status
  const handleToggleArchive = useCallback(
    async (submission: FormSubmission) => {
      if (!siteId) return;

      setIsToggling(true);
      try {
        const updated = await submissionsApi.toggleArchive(
          siteId,
          submission.id,
          !submission.isArchived
        );
        updateSubmissionInList(updated);
        showToast(updated.isArchived ? 'Submission archived' : 'Submission unarchived', 'success');
      } catch (error) {
        console.error('Failed to toggle archive:', error);
        showToast('Failed to update submission', 'error');
      } finally {
        setIsToggling(false);
      }
    },
    [siteId, updateSubmissionInList, showToast]
  );

  // Delete submission
  const handleDelete = useCallback(
    async (submission: FormSubmission) => {
      if (!siteId) return;

      setIsDeleting(true);
      try {
        await submissionsApi.deleteSubmission(siteId, submission.id);
        removeSubmissionFromList(submission.id);
        setSelectedSubmission(null);
        showToast('Submission deleted', 'success');
        // Reload to update stats
        loadSubmissions();
      } catch (error) {
        console.error('Failed to delete submission:', error);
        showToast('Failed to delete submission', 'error');
      } finally {
        setIsDeleting(false);
      }
    },
    [siteId, removeSubmissionFromList, setSelectedSubmission, loadSubmissions, showToast]
  );

  // View submission (opens detail modal and marks as read)
  const handleViewSubmission = useCallback(
    async (submission: FormSubmission) => {
      setSelectedSubmission(submission);
      if (!submission.isRead) {
        await handleMarkRead(submission);
      }
    },
    [setSelectedSubmission, handleMarkRead]
  );

  return {
    isMarkingRead,
    isToggling,
    isDeleting,
    toast,
    handleMarkRead,
    handleToggleArchive,
    handleDelete,
    handleViewSubmission,
    dismissToast: () => setToast(null),
  };
};
